/* eslint-disable @typescript-eslint/return-await */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/**
 * Worker Manager
 * Handles communication with web workers and manages their lifecycle
 */

import {
  CURR_ENV,
  STATIC_API_BASE_URL,
} from '@/core/constants/appConfig/environment';
import { EScriptEnv } from '@/core/constants/appConfig/types';
import { Logger } from '@/shared/logger/logger';

/**
 * Task sent to a worker
 */
export type WorkerTask = {
  id: string;
  origin?: string;
  payload: any;
  type: string;
};

/**
 * Response received from a worker
 */
export type WorkerResponse = {
  data?: any;
  error?: string;
  id: string;
  origin?: string;
  success: boolean;
  type: string;
};

/**
 * Options for WorkerManager
 */
export type WorkerManagerOptions = {
  /**
   * Bundle name from which the worker is called
   * Used for proper labeling in worker logs
   */
  bundleName?: string;

  /**
   * Whether to disable logging
   */
  disableLogs?: boolean;

  /**
   * Main thread bundle name
   * When provided, will be combined with worker's bundle name for logging
   */
  mainThreadBundleName?: string;

  /**
   * Unique origin identifier to track tasks from this instance
   */
  origin?: string;
};

/**
 * WorkerManager class for handling worker communication
 */
export class WorkerManager {
  private worker: Worker | null = null;
  private readonly pendingTasks = new Map<
    string,
    // eslint-disable-next-line @typescript-eslint/ban-types
    { reject: Function; resolve: Function }
  >();
  private readonly workerUrl: string;
  private initialized = false;
  private usingBlobUrl = false;
  private blobUrl: string | null = null;
  private readonly disableLogs: boolean;
  private readonly origin: string;
  private readonly bundleName?: string;
  private readonly mainThreadBundleName?: string;

  /**
   * Creates a new worker manager
   * @param workerPath - Path to the worker script (can be filename, relative path, or full URL)
   * @param options - Configuration options for the worker manager
   */
  constructor(workerPath: string, options?: WorkerManagerOptions) {
    this.disableLogs = options?.disableLogs || false;
    // Include environment in origin to prevent cross-environment communication
    const envPrefix = `env_${CURR_ENV}_`;

    // Make the origin more unique by adding additional entropy
    // This helps prevent conflicts between multiple worker instances
    const randomPart = Math.random().toString(36).slice(2, 10);
    const timestamp = Date.now().toString(36);

    this.origin =
      options?.origin || `${envPrefix}wrk_mgr_${timestamp}_${randomPart}`;

    // Store bundle name if provided
    this.bundleName = options?.bundleName
      ? options.bundleName.toLowerCase()
      : undefined;

    // Store main thread bundle name if provided
    this.mainThreadBundleName = options?.mainThreadBundleName;

    // Check if the path is already a full URL
    if (workerPath.startsWith('http')) {
      this.workerUrl = workerPath;
    } else {
      // Extract filename if a path is provided
      const fileName = workerPath.split('/').pop() || workerPath;

      // Use dist folder in local or test environment, otherwise use static2
      if (CURR_ENV === EScriptEnv.TEST || CURR_ENV === EScriptEnv.LOCAL) {
        // Ensure the URL doesn't have a trailing slash
        const baseUrl = STATIC_API_BASE_URL.endsWith('/')
          ? STATIC_API_BASE_URL.slice(0, -1)
          : STATIC_API_BASE_URL;

        this.workerUrl = `${baseUrl}/dist/${fileName}`;
      } else {
        // For production and other environments, use static2 path
        // Ensure the URL doesn't have a trailing slash
        const baseUrl = STATIC_API_BASE_URL.endsWith('/')
          ? STATIC_API_BASE_URL.slice(0, -1)
          : STATIC_API_BASE_URL;

        this.workerUrl = `${baseUrl}/static2/${fileName}`;
      }
    }

    if (!this.disableLogs) {
      Logger.debug(`Worker URL set to: ${this.workerUrl}`);
      Logger.debug(`Worker origin ID: ${this.origin}`);
    }
  }

  /**
   * Create a worker using Blob URL to bypass CORS restrictions
   * @param url - The URL of the worker script
   * @returns A promise that resolves to a Worker instance
   */
  private async createWorkerFromBlob(url: string): Promise<Worker> {
    try {
      if (!this.disableLogs) {
        Logger.info(`Fetching worker script from ${url} to create blob URL`);
      }

      // Add cache-busting parameter to avoid cached responses
      const fetchUrl = url.includes('?')
        ? `${url}&_t=${Date.now()}`
        : `${url}?_t=${Date.now()}`;

      const response = await fetch(fetchUrl, {
        credentials: 'omit',
        headers: {
          Accept: 'application/javascript',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        },
        keepalive: true,
        // we can't use no-cors here because we need to access the worker script!!
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch worker script: ${response.status} ${response.statusText} from URL: ${fetchUrl}`,
        );
      }

      let workerCode = await response.text();

      if (!workerCode || workerCode.trim().length === 0) {
        throw new Error(
          `Worker script is empty or invalid from URL: ${fetchUrl}`,
        );
      }

      // Log the first 100 characters of the worker script for debugging
      if (!this.disableLogs) {
        Logger.debug(
          `Worker script first 100 chars: ${workerCode.slice(0, 100)}...`,
        );
      }

      // If main thread bundle name is provided, include it in the worker's bundle name format
      const envInjection = `
        // Debug logging for worker
        console.log("Worker script initialized with URL: ${url}");
        // End of injected code
      `;

      workerCode = envInjection + workerCode;

      // Create a blob URL from the worker code
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      this.blobUrl = URL.createObjectURL(blob);

      if (!this.disableLogs) {
        Logger.info(`Created blob URL for worker with environment ${CURR_ENV}`);
      }
      this.usingBlobUrl = true;

      return new Worker(this.blobUrl);
    } catch (error) {
      if (!this.disableLogs) {
        Logger.error(`Failed to create worker from blob:`, error);
        Logger.error(`Worker blob creation details:`, {
          bundleName: this.bundleName || 'unknown',
          environment: CURR_ENV,
          mainThreadBundleName: this.mainThreadBundleName || 'unknown',
          origin: this.origin,
          url,
        });
      }

      // Attach worker details to the error object for better error handling upstream
      const workerDetails = {
        blobUrl: null,
        bundleName: this.bundleName || 'unknown',
        environment: CURR_ENV,
        fetchMethod: 'blob',
        mainThreadBundleName: this.mainThreadBundleName || 'unknown',
        origin: this.origin,
        usingBlobUrl: true,
        workerUrl: url,
      };

      if (error instanceof Error) {
        (error as any).workerDetails = workerDetails;
      }

      throw error;
    }
  }

  /**
   * Initialize the worker if not already initialized
   * @returns The worker instance
   */
  private async initWorker(): Promise<Worker> {
    if (!this.worker) {
      try {
        if (!this.disableLogs) {
          Logger.info(`Initializing worker from ${this.workerUrl}`);
        }

        // Append environment to the URL if not already present
        let workerUrl = this.workerUrl;

        // Add env parameter
        if (!workerUrl.includes('env=') && !workerUrl.includes('?')) {
          workerUrl += `?env=${CURR_ENV}`;
        } else if (!workerUrl.includes('env=') && workerUrl.includes('?')) {
          workerUrl += `&env=${CURR_ENV}`;
        }

        // Add bundle name parameter if available
        if (this.bundleName && !workerUrl.includes('bundle=')) {
          workerUrl += `&bundle=${this.bundleName}`;
        }

        // Add main thread bundle name parameter if available
        if (this.mainThreadBundleName && !workerUrl.includes('mainBundle=')) {
          workerUrl += `&mainBundle=${this.mainThreadBundleName}`;
        }

        // Add origin parameter
        if (!workerUrl.includes('origin=')) {
          workerUrl += `&origin=${this.origin}`;
        }

        // Removed cache busting parameter to optimize loading and leverage browser caching
        // Workers are typically static and should be cached by the browser for better performance

        if (!this.disableLogs) {
          Logger.debug(`Attempting to load worker from: ${workerUrl}`);
        }

        // Flag to track if we should try the blob approach immediately
        const useBlobFirst =
          // If we're in a non-local environment or the URL is external
          (CURR_ENV !== EScriptEnv.LOCAL && !workerUrl.startsWith('/')) ||
          // Or if we've detected CORS issues previously in the same session
          document.cookie.includes('worker_use_blob=true');

        if (useBlobFirst) {
          if (!this.disableLogs) {
            Logger.info(
              'Using blob approach directly due to previous CORS issues or non-local environment',
            );
          }
          this.worker = await this.createWorkerFromBlob(workerUrl);
        } else {
          try {
            // Try direct initialization
            this.worker = new Worker(workerUrl);
          } catch (directError) {
            // If error contains CORS or network-related issues, try the blob approach
            if (
              directError instanceof DOMException &&
              (directError.name === 'SecurityError' ||
                directError.name === 'NetworkError' ||
                directError.message.includes('origin'))
            ) {
              if (!this.disableLogs) {
                Logger.warn(
                  `Direct worker initialization failed with CORS error, trying blob URL approach: ${directError.message}`,
                );
              }

              // Set a cookie to remember to use blob approach next time
              // eslint-disable-next-line unicorn/no-document-cookie
              document.cookie = 'worker_use_blob=true; path=/; max-age=3600';

              this.worker = await this.createWorkerFromBlob(workerUrl);
            } else {
              // Re-throw if it's not a CORS issue
              throw directError;
            }
          }
        }

        this.worker.addEventListener(
          'message',
          (event: MessageEvent<WorkerResponse>) => {
            const response = event.data;

            // Additional debug logging for ALL responses
            if (!this.disableLogs) {
              Logger.debug(
                `[WorkerManager:${this.origin}] Received worker message with origin: ${response.origin || 'undefined'}, id: ${response.id}, type: ${response.type}`,
              );
            }

            // Check if this response belongs to this manager instance by checking origin
            if (response.origin && response.origin !== this.origin) {
              if (!this.disableLogs) {
                Logger.debug(
                  `[WorkerManager:${this.origin}] Ignoring response from different origin: ${response.origin}`,
                );
              }
              return;
            }

            // Also check if we have a pending task for this response ID
            // This prevents duplicate processing in case multiple instances end up receiving the message
            const pendingTask = this.pendingTasks.get(response.id);
            if (!pendingTask) {
              if (!this.disableLogs) {
                Logger.debug(
                  `[WorkerManager:${this.origin}] No pending task found for response ID: ${response.id}, ignoring response`,
                );
              }
              return;
            }

            if (!this.disableLogs) {
              Logger.info(
                `[WorkerManager:${this.origin}] Received response - ${Logger.getTimeSinceScriptStart()}ms since init`,
                response,
              );
            }

            this.pendingTasks.delete(response.id);

            if (response.success) {
              pendingTask.resolve(response.data);
            } else {
              pendingTask.reject(new Error(response.error));
            }
          },
        );

        this.worker.addEventListener('error', error => {
          if (!this.disableLogs) {
            Logger.error('Worker error:', error);
            Logger.error('Worker error details:', {
              blobUrl: this.blobUrl || 'not_used',
              bundleName: this.bundleName || 'unknown',
              environment: CURR_ENV,
              errorDetails: {
                colno: error.colno,
                filename: error.filename,
                lineno: error.lineno,
                message: error.message,
              },
              origin: this.origin,
              usingBlobUrl: this.usingBlobUrl,
              workerUrl: this.workerUrl,
            });
          }
        });

        // Wait for worker to initialize
        await new Promise<void>(resolve => {
          setTimeout(resolve, 0);
        });

        this.initialized = true;
      } catch (error) {
        if (!this.disableLogs) {
          Logger.error('Failed to initialize worker:', error);
          Logger.error('Worker initialization details:', {
            blobUrl: this.blobUrl || 'not_used',
            bundleName: this.bundleName || 'unknown',
            environment: CURR_ENV,
            mainThreadBundleName: this.mainThreadBundleName || 'unknown',
            origin: this.origin,
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          });
        }

        // Attach worker details to the error object for better error handling upstream
        const workerDetails = {
          blobUrl: this.blobUrl || 'not_used',
          bundleName: this.bundleName || 'unknown',
          environment: CURR_ENV,
          mainThreadBundleName: this.mainThreadBundleName || 'unknown',
          origin: this.origin,
          usingBlobUrl: this.usingBlobUrl,
          workerUrl: this.workerUrl,
        };

        if (error instanceof Error) {
          (error as any).workerDetails = workerDetails;
        }

        throw error;
      }
    }

    return this.worker;
  }

  /**
   * Execute a task in the worker and return a promise that resolves with the result
   * @param type - Task type
   * @param payload - Task payload
   * @returns Promise that resolves with the worker's response
   */
  public async executeTask(type: string, payload: any): Promise<any> {
    try {
      const worker = await this.initWorker();

      const taskId =
        Date.now().toString() + Math.random().toString(36).slice(2, 9);

      if (!this.disableLogs) {
        Logger.info(`[WorkerManager:${this.origin}] Executing task ${type}:`, {
          payload,
          taskId,
        });
      }

      return new Promise((resolve, reject) => {
        this.pendingTasks.set(taskId, { reject, resolve });

        const task: WorkerTask = {
          id: taskId,
          origin: this.origin, // Add origin to identify tasks from this instance
          payload,
          type,
        };

        if (!this.disableLogs) {
          Logger.debug(
            `[WorkerManager:${this.origin}] Sending task to worker:`,
            {
              id: taskId,
              origin: this.origin,
              type,
            },
          );
        }

        // eslint-disable-next-line unicorn/require-post-message-target-origin
        worker.postMessage(task);
      });
    } catch (error) {
      if (!this.disableLogs) {
        Logger.error(
          `[WorkerManager:${this.origin}] Task execution failed for ${type}:`,
          error,
          {
            blobUrl: this.blobUrl,
            bundleName: this.bundleName || 'unknown',
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          },
        );
      }

      // Attach worker details to the error object for better error handling upstream
      const workerDetails = {
        blobUrl: this.blobUrl,
        bundleName: this.bundleName || 'unknown',
        environment: CURR_ENV,
        origin: this.origin,
        usingBlobUrl: this.usingBlobUrl,
        workerUrl: this.workerUrl,
      };

      if (error instanceof Error) {
        (error as any).workerDetails = workerDetails;
      }

      throw error;
    }
  }

  /**
   * Submit a task to the worker without waiting for the result
   * @param type - Task type
   * @param payload - Task payload
   * @param onComplete - Optional callback function to handle the result when it completes
   * @returns The task ID that can be used to track the task
   */
  public async submitTask(
    type: string,
    payload: any,
    onComplete?: (error: Error | null, data?: any) => void,
  ): Promise<string> {
    try {
      const worker = await this.initWorker();

      const taskId =
        Date.now().toString() + Math.random().toString(36).slice(2, 9);

      if (!this.disableLogs) {
        Logger.info(`[WorkerManager:${this.origin}] Submitting task ${type}:`, {
          payload,
          taskId,
        });
      }

      // If a completion callback is provided, register it
      if (onComplete) {
        this.pendingTasks.set(taskId, {
          reject: (error: Error) => onComplete(error),
          resolve: (data: any) => onComplete(null, data),
        });
      }

      const task: WorkerTask = {
        id: taskId,
        origin: this.origin,
        payload,
        type,
      };

      if (!this.disableLogs) {
        Logger.debug(
          `[WorkerManager:${this.origin}] Sending background task to worker:`,
          {
            id: taskId,
            origin: this.origin,
            type,
          },
        );
      }

      // eslint-disable-next-line unicorn/require-post-message-target-origin
      worker.postMessage(task);

      // Return the task ID immediately
      return taskId;
    } catch (error) {
      if (!this.disableLogs) {
        Logger.error(
          `[WorkerManager:${this.origin}] Task submission failed for ${type}:`,
          error,
          {
            blobUrl: this.blobUrl,
            bundleName: this.bundleName || 'unknown',
            usingBlobUrl: this.usingBlobUrl,
            workerUrl: this.workerUrl,
          },
        );
      }

      throw error;
    }
  }

  /**
   * Check if the worker is initialized
   * @returns True if the worker is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Terminate the worker
   */
  public terminate(): void {
    if (this.worker) {
      if (!this.disableLogs) {
        Logger.info('Terminating worker');
      }
      this.worker.terminate();
      this.worker = null;
      this.initialized = false;

      // Release blob URL if it was created
      if (this.usingBlobUrl && this.blobUrl) {
        try {
          URL.revokeObjectURL(this.blobUrl);
          if (!this.disableLogs) {
            Logger.info('Revoked blob URL');
          }
          this.blobUrl = null;
        } catch (error) {
          // Ignore errors when trying to revoke URL
          if (!this.disableLogs) {
            Logger.warn('Failed to revoke blob URL', error);
          }
        }
      }

      this.usingBlobUrl = false;
    }
  }
}
